import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { debounceTime, firstValueFrom, Observable, Subscription } from 'rxjs';
import { isEqual } from 'lodash';

import {
  ConfTypes,
  Equipment,
  Locations,
  ParticipantFsDaily,
  ParticipantFsDolby,
  Prompt,
  PromptTypes,
} from '@sc/types';
import { UserService } from '../../services/user/user.service';
import { SessionsService } from '../../services/sessions/sessions.service';
import { MuteService } from '../../services/mute/mute.service';
import { VideoOffService } from '../../services/video-off/video-off.service';
import { EquipmentService } from '../../services/equipment/equipment.service';
import { ParticipantMenuComponent } from '../../menus/participant-menu/participant-menu.component';
import { DolbyService } from '../../services/dolby/dolby.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { NameService } from '../../services/name/name.service';
import { InvitationLinkMenuComponent } from '../../menus/invitation-link-menu/invitation-link-menu.component';
import { RoleLimits } from '@sc/types';
import { RolesService } from '../../services/roles/roles.service';
import { defaultPopoverOptions } from '../../services/theme/theme.service';
import { DailyService } from '../../services/daily/daily.service';

@Component({
  selector: 'sc-participants',
  templateUrl: './participants.component.html',
  styleUrls: ['./participants.component.scss'],
})
export class ParticipantsComponent implements OnInit, OnDestroy {
  @Output() closeSidebar = new EventEmitter();
  userData$ = this.userService.userData$;
  activeUser$ = this.userService.activeUser$;
  studioSession$ = this.sessionsService.studioSession$;
  role$ = this.rolesService.role$;
  roleLimits = RoleLimits;
  onStageDolby: Array<ParticipantFsDolby & { name$: Observable<string> }> = []; // we need 2 local arrays to drag and drop between
  backstageDolby: Array<ParticipantFsDolby & { name$: Observable<string> }> = [];
  participantsDaily: Array<ParticipantFsDaily & { name$: Observable<string> }> = [];
  subs: Array<Subscription> = [];

  constructor(
    private popoverController: PopoverController,
    private userService: UserService,
    private sessionsService: SessionsService,
    private muteService: MuteService,
    private videoOffService: VideoOffService,
    private equipmentService: EquipmentService,
    private dolbyService: DolbyService,
    public rolesService: RolesService,
    private analyticsService: AnalyticsService,
    private nameService: NameService,
    private dailyService: DailyService
  ) {}

  /**
   * Initializes the studio sidebar component
   */
  ngOnInit() {
    this.setupDolbyParticipants();
    this.setupDailyParticipants();
  }

  async openInvitationLinkMenu(click: MouseEvent, defaultLocation?: 'The Stage' | 'Backstage') {
    const menu = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: InvitationLinkMenuComponent,
      componentProps: { defaultLocation, session: this.studioSession$.value },
      cssClass: 'open-invitation-link-menu',
      event: click,
    });
    await menu.present();
    await this.analyticsService.track('opened participant invitation link');
  }

  /**
   * Toggles mute for a given participant
   *
   * @param participant
   */
  async toggleParticipantMute(participant: ParticipantFsDolby | ParticipantFsDaily) {
    const equipment: Equipment = await firstValueFrom(participant.equipment$);
    const uid =
      this.studioSession$.value?.confType === ConfTypes.DAILY
        ? (participant as ParticipantFsDaily).userData.scUID
        : participant.uid;
    if (equipment.mute) {
      if (this.userData$.value.uid !== uid) {
        const name = await firstValueFrom(
          this.nameService.getName(this.studioSession$.value.sessionID, this.activeUser$.value.uid)
        );
        const prompt: Prompt = {
          title: `${name} is requesting that you Unmute your Microphone`,
          message: `Do you want to Unmute your Microphone?`,
          creator: this.activeUser$.value.uid,
          presentAs: `toast`,
          type: PromptTypes.UNMUTEMICROPHONE,
          targetParticipants: [uid],
        };

        await this.sessionsService.createSessionPrompt(prompt);
        await this.analyticsService.track('prompted participant to unmute', {
          creatorUID: this.activeUser$.value.uid,
          targetParticipantUID: uid,
        });
      } else {
        this.muteService.setMute(this.studioSession$.value.sessionID, uid, !equipment.mute);
        await this.analyticsService.track('toggled muted', { enabled: !equipment.mute });
      }
    } else {
      this.muteService.setMute(this.studioSession$.value.sessionID, uid, !equipment.mute);
      await this.analyticsService.track('muted participant', {
        creatorUID: this.activeUser$.value.uid,
        targetParticipantUID: uid,
      });
    }
  }

  /**
   * Toggles video off for a given participant
   *
   * @param participant
   */
  async toggleParticipantVideoOff(participant: ParticipantFsDolby | ParticipantFsDaily) {
    const equipment: Equipment = await firstValueFrom(participant.equipment$);
    const uid =
      this.studioSession$.value?.confType === ConfTypes.DAILY
        ? (participant as ParticipantFsDaily).userData.scUID
        : participant.uid;

    if (equipment.videoOff) {
      if (this.userData$.value.uid !== uid) {
        const name = await firstValueFrom(
          this.nameService.getName(this.studioSession$.value.sessionID, this.activeUser$.value.uid)
        );
        const prompt: Prompt = {
          title: `${name} is requesting that you Enable your Camera`,
          message: `Do you want to Enable your Camera?`,
          creator: this.activeUser$.value.uid,
          presentAs: `toast`,
          type: PromptTypes.ENABLECAMERA,
          targetParticipants: [uid],
        };

        await this.sessionsService.createSessionPrompt(prompt);
        await this.analyticsService.track('prompted participant to enable camera', {
          creatorUID: this.activeUser$.value.uid,
          targetParticipantUID: uid,
        });
      } else {
        this.videoOffService.setVideoOff(this.studioSession$.value.sessionID, uid, !equipment.videoOff);
        await this.analyticsService.track('toggled video off', { enabled: !equipment.videoOff });
      }
    } else {
      this.videoOffService.setVideoOff(this.studioSession$.value.sessionID, uid, !equipment.videoOff);
      await this.analyticsService.track('disabled participant camera', {
        creatorUID: this.activeUser$.value.uid,
        targetParticipantUID: uid,
      });
    }
  }

  /**
   * Updates the participant's volume
   *
   * @param participant
   */
  updateVolume(participant: ParticipantFsDolby) {
    this.equipmentService.setMicrophoneVolume(this.studioSession$.value.sessionID, participant.uid, participant.volume);
  }

  async openParticipantMenu(
    click: MouseEvent,
    participant: ParticipantFsDolby | ParticipantFsDaily,
    appearance: 'the stage' | 'backstage'
  ) {
    const uid =
      this.studioSession$.value?.confType === ConfTypes.DAILY
        ? (participant as ParticipantFsDaily).userData.scUID
        : participant.uid;

    let p =
      this.studioSession$.value?.confType === ConfTypes.DAILY
        ? (participant as ParticipantFsDaily)
        : (participant as ParticipantFsDolby);

    const menu: HTMLIonPopoverElement = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: ParticipantMenuComponent,
      componentProps: { participant: p, appearance },
      cssClass: 'participant-menu',
      event: click,
    });
    await menu.present();

    await this.analyticsService.track('opened manage participant', { participantUID: uid });
  }

  /**
   * Expands the defaultVolume range input open and closed for a given participant
   *
   * @param participant
   */
  expand(participant: ParticipantFsDolby) {
    participant.expanded = true;
  }

  /**
   * Expands the defaultVolume range input open and closed for a given participant
   *
   * @param participant
   */
  collapse(participant: ParticipantFsDolby) {
    participant.expanded = false;
  }

  /**
   * Sorts the participants into stage and backstage
   */
  setupDolbyParticipants() {
    const debouncedParticipants = this.dolbyService.stageParticipants$.pipe(debounceTime(500));
    const onStageSub = debouncedParticipants.subscribe((stageParticipants) => {
      this.onStageDolby.forEach((p) => {
        if (!stageParticipants.has(p.uid)) {
          this.onStageDolby.splice(this.onStageDolby.indexOf(p), 1);
        }
      });
      stageParticipants.forEach((participant: ParticipantFsDolby & { name$: Observable<string> }) => {
        if (!this.onStageDolby.find((p) => p.uid === participant.uid)) {
          participant.location = Locations.STAGE;
          participant.name$ = this.nameService.getName(this.studioSession$.value.sessionID, participant.uid);
          participant.equipment$ = this.equipmentService.getParticipantEquipment(
            participant.uid,
            this.studioSession$.value.sessionID
          );
          participant.photoURL = participant.info.avatarUrl;
          this.onStageDolby.push(participant);
        }
      });
    });

    const backstageSub = this.dolbyService.backstageParticipantsArray$.subscribe((participants) => {
      this.backstageDolby.forEach((p) => {
        if (!participants.find((participant) => participant.uid === p.uid)) {
          this.backstageDolby.splice(this.backstageDolby.indexOf(p), 1);
        }
      });
      participants.forEach((participant: ParticipantFsDolby & { name$: Observable<string> }) => {
        if (!this.backstageDolby.find((p) => p.uid === participant.uid)) {
          participant.location = Locations.BACKSTAGE;
          participant.name$ = this.nameService.getName(this.studioSession$.value.sessionID, participant.uid);
          participant.photoURL = participant.info.avatarUrl;
          this.backstageDolby.push(participant);
        }
      });
    });

    this.subs.push(onStageSub);
    this.subs.push(backstageSub);
  }

  setupDailyParticipants() {
    const debouncedParticipants = this.dailyService.stageParticipants$.pipe(debounceTime(500));
    const sub = debouncedParticipants.subscribe((participants) => {
      // remove participants that are no longer on stage
      this.participantsDaily.forEach((p) => {
        if (!participants.has(p.userData.scUID)) {
          this.participantsDaily.splice(this.participantsDaily.indexOf(p), 1);
        }
      });

      participants.forEach((participant: ParticipantFsDaily & { name$: Observable<string> }) => {
        participant.name$ = this.nameService.getName(this.studioSession$.value.sessionID, participant.userData.scUID);
        participant.equipment$ = this.equipmentService.getParticipantEquipment(
          participant.userData.scUID,
          this.studioSession$.value.sessionID
        );

        // Find the index of the participant in the array
        const participantIndex = this.participantsDaily.findIndex(
          (p) => p.userData.scUID === participant.userData.scUID
        );

        // If participant exists, update it only if something has changed, else push it to the array
        if (participantIndex !== -1) {
          if (!isEqual(this.participantsDaily[participantIndex], participant)) {
            this.participantsDaily[participantIndex] = participant;
          }
        } else {
          this.participantsDaily.push(participant);
        }
      });
    });

    this.subs.push(sub);
  }

  trackByParticipantDaily(index: number, participant: ParticipantFsDaily) {
    return participant.userData.scUID;
  }

  /**
   * Prevents flickering/flashing in the list of on stage participants
   *
   * @param index -number
   * @param participant - ParticipantFsDolby
   * @returns - string
   */
  trackByOnStageDolby(index: number, participant: ParticipantFsDolby) {
    return participant.uid;
  }

  /**
   * Prevents flickering/flashing in the list of backstage participants
   *
   * @param index -number
   * @param participant - ParticipantFsDolby
   * @returns - string
   */
  trackByBackstageDolby(index: number, participant: ParticipantFsDolby) {
    return participant.uid;
  }

  /**
   * Unsubscribes from all observables
   */
  ngOnDestroy() {
    this.subs.map((sub) => {
      sub.unsubscribe();
    });
  }
}
